import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Input,
  Table,
  TableCaption,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tfoot,
  TableContainer,
  List,
  ListItem,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Heading,
  Text,
  Grid,
  GridItem,
  Divider,
  HStack,
  VStack,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

const DailySalesSummary = () => {
  const [data, setData] = useState([]);
  const [wineData, setWineData] = useState([]);
  const [payment, setPayment] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const [foodData, setFoodData] = useState([]);
  const [filteredFoodData, setFilteredFoodData] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [toggle, setToggle] = useState(false);
  
  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();

  const customerId = localStorage.getItem("customerId");

  const handleToggle = () => {
    setToggle(!toggle);
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `https://api.rcb.tdrsoftware.in/api/getSalesSummary/${customerId}?startDate=${startDate}&endDate=${endDate}&searchItem=${searchItem}`
      );
      setData(response.data.foodSales);
      setWineData(response.data.wineSales);
      setPayment(response.data.paymentSummary);
    } catch (error) {
      toast.error("Error fetching data");
    }
  };

  const fetchFoodData = async () => {
    try {
      const response = await axios.get(
        `https://api.rcb.tdrsoftware.in/api/getFoodAndWineData/${customerId}`
      );
      setFoodData(response.data.items);
      setFilteredFoodData(response.data.items);
    } catch (error) {
      toast.error("Error fetching food data");
    }
  };

  useEffect(() => {
    fetchData();
    fetchFoodData();
  }, [customerId, startDate, endDate, searchItem]);

  const updateBillDates = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    const customerId = localStorage.getItem("customerId");

    try {
      await axios.put(
        `https://api.rcb.tdrsoftware.in/api/updateCreatedAt/${customerId}`,
        {
          fromDate: startDate,
          toDate: endDate,
        }
      );
      toast.success("Bill dates updated successfully");
    } catch (error) {
      toast.error("Failed to update bill dates");
    }
  };

  const consolidateDataForExcel = () => {
    const consolidatedData = [];
    const maxRows = Math.max(data.length, wineData.length, 1);

    let totalFoodQuantity = 0;
    let totalFoodAmount = 0;
    let totalwineQuantity = 0;
    let totalwineAmount = 0;

    for (let i = 0; i < maxRows; i++) {
      totalFoodQuantity += data[i]?.totalQuantity || 0;
      totalFoodAmount += data[i]?.totalAmount || 0;
      totalwineQuantity += wineData[i]?.totalQuantity || 0;
      totalwineAmount += wineData[i]?.totalAmount || 0;

      consolidatedData.push({
        Food_Item: data[i]?._id || "",
        Food_Rate: data[i]?.rate || "",
        Food_TotalQuantity: data[i]?.totalQuantity || "",
        Food_TotalAmount: data[i]?.totalAmount || "",
        Wine_Item: wineData[i]?._id || "",
        Wine_Rate: wineData[i]?.rate || "",
        Wine_TotalQuantity: wineData[i]?.totalQuantity || "",
        Wine_TotalAmount: wineData[i]?.totalAmount || "",
        Cash: i === 0 ? payment.cashTotal || "" : "",
        Online: i === 0 ? payment.onlineTotal || "" : "",
        Card: i === 0 ? payment.cardTotal || "" : "",
        Discount: i === 0 ? payment.discountTotal || "" : "",
        HappyDiscount: i === 0 ? payment.happyHourDiscount || "" : "",
        Gst: i === 0 ? payment.tax || "" : "",
        Due: i === 0 ? payment.dueTotal || "" : "",
        NetAmount:
          i === 0
            ? payment.cashTotal +
                payment.onlineTotal +
                payment.cardTotal +
                payment.tax || ""
            : "",
      });
    }

    consolidatedData.push({
      Food_Item: "Total",
      Food_TotalQuantity: totalFoodQuantity,
      Food_TotalAmount: totalFoodAmount,
      Wine_TotalQuantity: totalwineQuantity,
      Wine_TotalAmount: totalwineAmount,
    });

    return consolidatedData;
  };

  const exportToExcel = () => {
    const dataToExport = consolidateDataForExcel();
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Sales Summary");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${startDate}-${endDate}-DailySalesSummary.xlsx`);
  };

  const exportToPDF = () => {
    const dataToExport = consolidateDataForExcel();
    const pdf = new jsPDF();
    const columns = [
      { header: "Food Item", dataKey: "Food_Item" },
      { header: "Food Rate", dataKey: "Food_Rate" },
      { header: "Food Total Quantity", dataKey: "Food_TotalQuantity" },
      { header: "Food Total Amount", dataKey: "Food_TotalAmount" },
      { header: "Wine Item", dataKey: "Wine_Item" },
      { header: "Wine Rate", dataKey: "Wine_Rate" },
      { header: "Wine Total Quantity", dataKey: "Wine_TotalQuantity" },
      { header: "Wine Total Amount", dataKey: "Wine_TotalAmount" },
      { header: "Cash", dataKey: "Cash" },
      { header: "Online", dataKey: "Online" },
      { header: "Card", dataKey: "Card" },
      { header: "Discount", dataKey: "Discount" },
      { header: "HappyDiscount", dataKey: "HappyDiscount" },
      { header: "Gst", dataKey: "Gst" },
      { header: "Due", dataKey: "Due" },
      { header: "Net Amount", dataKey: "NetAmount" },
    ];

    pdf.text("Daily Sales Summary", 14, 20);
    pdf.autoTable({
      startY: 30,
      head: [columns.map((col) => col.header)],
      body: dataToExport.map((row) =>
        columns.map((col) => row[col.dataKey] || "")
      ),
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 57, 107], textColor: [255, 255, 255] },
    });

    pdf.save(`${startDate}-${endDate}DailySalesSummary.pdf`);
  };

  useEffect(() => {
    if (searchItem) {
      const filtered = foodData.filter((item) =>
        item.name.toLowerCase().includes(searchItem.toLowerCase())
      );
      setFilteredFoodData(filtered);
    } else {
      setFilteredFoodData(foodData);
    }
    setHighlightedIndex(-1);
  }, [searchItem, foodData]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) =>
        prev < filteredFoodData.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredFoodData.length - 1
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      setSearchItem(filteredFoodData[highlightedIndex].name);
      setFilteredFoodData([]);
    }
  };

  const generateTableHtml = (items) => {
    const totalQty = items.reduce((sum, item) => sum + item.totalQuantity, 0);
    const totalAmt = items.reduce((sum, item) => sum + item.totalAmount, 0);

    return `
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Rate</th>
          <th>Qty</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${items
          .map(
            (item) => `
          <tr>
            <td>${item._id}</td>
            <td>${item.rate}</td>
            <td>${item.totalQuantity}</td>
            <td>${item.totalAmount}</td>
          </tr>`
          )
          .join("")}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="2" style="text-align: right;">Total Qty:</td>
          <td>${totalQty}</td>
          <td>${totalAmt}</td>
        </tr>
      </tfoot>
    </table>
  `;
  };

  const generatePaymentTableHtml = (payment) => {
    const netAmount =
      payment.cashTotal + payment.onlineTotal + payment.cardTotal;

    return `
    <table style="font-size: 16px;">
      <thead>
        <tr>
          <th style="font-size: 18px;">Cash</th>
          <th style="font-size: 18px;">Online</th>
          <th style="font-size: 18px;">Card</th>
          <th style="font-size: 18px;">Discount</th>
          <th style="font-size: 18px;">Happy Discount</th>
          <th style="font-size: 18px;">Gst</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="font-size: 16px;">${payment.cashTotal}</td>
          <td style="font-size: 16px;">${payment.onlineTotal}</td>
          <td style="font-size: 16px;">${payment.cardTotal}</td>
          <td style="font-size: 16px;">${payment?.discountTotal?.toFixed(2)}</td>
          <td style="font-size: 16px;">${payment?.happyHourDiscount?.toFixed(2)}</td>
          <td style="font-size: 16px;">${Number(payment.gstTotal).toFixed(1)}</td>
        </tr>
        <tr>
          <td colspan="4"></td>
          <td style="font-size: 16px;"><strong>Due</strong></td>
          <td style="font-size: 16px;">${payment.dueTotal}</td>
        </tr>
        <tr>
          <td colspan="4"></td>
          <td style="font-size: 18px;"><strong>Net Amount</strong></td>
          <td style="font-size: 18px;">${netAmount}</td>
        </tr>
      </tbody>
    </table>
  `;
  };

  const handlePrint = (type) => {
    let content = "";

    if (type === "food") {
      content = generateTableHtml(data, "Food Sales Summary");
    } else if (type === "wine") {
      content = generateTableHtml(wineData, "Wine Sales Summary");
    } else if (type === "total") {
      content = `
      <h3>Food Sales Summary</h3>
      ${generateTableHtml(data)}
      <h3>Wine Sales Summary</h3>
      ${generateTableHtml(wineData)}
      <h3>Payment Summary</h3>
      ${generatePaymentTableHtml(payment)}
    `;
    } else if (type === "payment") {
      content = generatePaymentTableHtml(payment);
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
    <html>
      <head>
        <title>Print ${
          type.charAt(0).toUpperCase() + type.slice(1)
        } Summary</title>
        <style>
          @media print {
            body {
              margin: 0;
              padding: 0;
              font-family: sans-serif;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
              margin-bottom: 12px;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
            }
            th {
              background-color: #EDF2F7;
            }
            h2, h3, p {
              margin: 4px 0;
              text-align: center;
              font-size: 13px;
            }
            @page {
              margin: 5mm;
            }
          }
        </style>
      </head>
      <body>
        <h2>${
          type === "total"
            ? "Sales Ledger Summary"
            : type === "payment"
            ? "Payment Summary"
            : type.charAt(0).toUpperCase() + type.slice(1) + " Ledger Summary"
        }</h2>
        <p>${new Date().toLocaleDateString()} - ${new Date().toLocaleTimeString()}</p>
        ${content}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Function to calculate food analysis report values
  const calculateFoodAnalysis = () => {
    const {
      totalFoodPrice = 0,
      discountTotal = 0,
      foodTaxableAmount = 0,
      foodCgstTotal = 0,
      foodSgstTotal = 0,
      foodRoundOff = 0,
      netFoodSale = 0,
      foodNetAmount = 0,
      cashTotal = 0,
      dueTotal = 0,
      onlineTotal = 0,
      cardTotal = 0
    } = payment;

    const foodNonTaxableAmount=totalFoodPrice-discountTotal;
    const netFoodSaleTaxable = netFoodSale - foodNonTaxableAmount;
    const addFoodSaleNontaxable = netFoodSaleTaxable + foodNonTaxableAmount;
    const settlementTotal = cashTotal + onlineTotal + cardTotal;
    return {
      // Food Section
      totalGrossFoodSale: totalFoodPrice,
      totalFoodDiscount: discountTotal,
      totalSaleNonTaxable: foodNonTaxableAmount,
      netFoodSaleTaxable: netFoodSaleTaxable,
      foodCGST: foodCgstTotal,
      foodSGST: foodSgstTotal,
      foodRoundOff: foodRoundOff,
      addFoodSaleNontaxable: addFoodSaleNontaxable,
      foodSaleNetAmount: foodNonTaxableAmount || (netFoodSale + foodCgstTotal + foodSgstTotal + foodRoundOff),
      
      // Settlement Section
      cash: cashTotal,
      dues: dueTotal,
      online: onlineTotal,
      card: cardTotal,
      settlementTotal: settlementTotal
    };
  };

  const analysisData = calculateFoodAnalysis();

  return (
    <Box>
      {/* Analyze Report Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading size="md" textAlign="center">
              JHANDA HINDU HOTEL - SALES ANALYZE REPORT
            </Heading>
            <Text fontSize="sm" textAlign="center" color="gray.600">
              FROM {startDate || "________"} TO {endDate || "________"}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* FOOD ANALYSIS SECTION */}
              <Box>
                <Heading size="md" mb={4} textAlign="center" color="blue.700">
                  *FOOD*
                </Heading>
                <Grid templateColumns="2fr 1fr" gap={2}>
                  <GridItem>
                    <Text fontWeight="bold">Total Gross Food Sale</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>{analysisData.totalGrossFoodSale.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem>
                    <Text fontWeight="bold">Total Food Discount</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text color="red.500">-{analysisData.totalFoodDiscount.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem>
                    <Text fontWeight="bold">Total Sale Non-taxable</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>{analysisData.totalSaleNonTaxable.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Divider />
                  </GridItem>

                  <GridItem>
                    {/* <Text fontWeight="bold" color="green.600">
                      Net Food Sale Taxable
                    </Text> */}
                  </GridItem>
                  <GridItem textAlign="right">
                    {/* <Text fontWeight="bold" color="green.600">
                      {analysisData.netFoodSaleTaxable.toFixed(2)}
                    </Text> */}
                  </GridItem>

                  <GridItem>
                    <Text>Food CGST</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>00</Text>
                  </GridItem>

                  <GridItem>
                    <Text>Food SGST</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>00</Text>
                  </GridItem>

                  <GridItem>
                    <Text>Food round off</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>±{analysisData.foodRoundOff.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Divider />
                  </GridItem>

                  <GridItem>
                    <Text fontWeight="bold">
                      Add Food Sale Non-taxable
                    </Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text fontWeight="bold">
                      +{analysisData.addFoodSaleNontaxable.toFixed(2)}
                    </Text>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Divider borderWidth="2px" />
                  </GridItem>

                  <GridItem>
                    <Text fontWeight="bold" fontSize="lg" color="purple.700">
                      Food Sale Net Amount
                    </Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text fontWeight="bold" fontSize="lg" color="purple.700">
                      {analysisData.foodSaleNetAmount.toFixed(2)}
                    </Text>
                  </GridItem>
                </Grid>
              </Box>

              {/* SETTLEMENT SECTION */}
              <Box>
                <Heading size="md" mb={4} textAlign="center" color="blue.700">
                  *Settlement*
                </Heading>
                <Grid templateColumns="2fr 1fr" gap={3}>
                  <GridItem>
                    <Text fontWeight="bold">TYPE</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text fontWeight="bold">Amount</Text>
                  </GridItem>

                  <GridItem>
                    <Text>CASH</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>{analysisData.cash.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem>
                    <Text>DUES</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text color="orange.500">{analysisData.dues.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem>
                    <Text>ONLINE</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>{analysisData.online.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem>
                    <Text>CARD</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text>{analysisData.card.toFixed(2)}</Text>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Divider borderWidth="2px" />
                  </GridItem>

                  <GridItem>
                    <Text fontWeight="bold" fontSize="lg">TOTAL</Text>
                  </GridItem>
                  <GridItem textAlign="right">
                    <Text fontWeight="bold" fontSize="lg">
                      {analysisData.settlementTotal.toFixed(2)}
                    </Text>
                  </GridItem>
                </Grid>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={() => {
              // Print the analysis report
              const printWindow = window.open("", "_blank");
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Food Sales Analysis Report</title>
                    <style>
                      body { font-family: Arial, sans-serif; margin: 20px; }
                      h1, h2 { text-align: center; }
                      .header { text-align: center; margin-bottom: 20px; }
                      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                      th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #ddd; }
                      th { background-color: #f2f2f2; }
                      .section { margin-bottom: 30px; }
                      .total-row { font-weight: bold; }
                      .divider { border-top: 2px solid #000; margin: 10px 0; }
                      @media print {
                        body { margin: 0; padding: 0; }
                        table { font-size: 12px; }
                      }
                    </style>
                  </head>
                  <body>
                    <div class="header">
                      <h1>JHANDA HINDU HOTEL</h1>
                      <h2>SALES ANALYZE REPORT</h2>
                      <p>FROM ${startDate || "________"} TO ${endDate || "________"}</p>
                    </div>
                    
                    <div class="section">
                      <h2>*FOOD*</h2>
                      <table>
                        <tr>
                          <td>Total Gross Food Sale</td>
                          <td>${analysisData.totalGrossFoodSale.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>Total Food Discount</td>
                          <td>-${analysisData.totalFoodDiscount.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>Total Sale Non-taxable</td>
                          <td>${analysisData.totalSaleNonTaxable.toFixed(2)}</td>
                        </tr>
                        <tr class="divider"></tr>
                        // <tr class="total-row">
                        //   <td>Net Food Sale Taxable</td>
                        //   <td>${analysisData.netFoodSaleTaxable.toFixed(2)}</td>
                        // </tr>
                        <tr>
                          <td>Food CGST</td>
                          <td>00</td>
                        </tr>
                        <tr>
                          <td>Food SGST</td>
                          <td>00</td>
                        </tr>
                        <tr>
                          <td>Food round off</td>
                          <td>±${analysisData.foodRoundOff.toFixed(2)}</td>
                        </tr>
                        <tr class="divider"></tr>
                        <tr class="total-row">
                          <td>Add Food Sale Non-taxable</td>
                          <td>+${analysisData.addFoodSaleNontaxable.toFixed(2)}</td>
                        </tr>
                        <tr class="total-row" style="border-top: 2px solid #000;">
                          <td><strong>Food Sale Net Amount</strong></td>
                          <td><strong>${analysisData.foodSaleNetAmount.toFixed(2)}</strong></td>
                        </tr>
                      </table>
                    </div>
                    
                    <div class="section">
                      <h2>*Settlement*</h2>
                      <table>
                        <tr>
                          <th>TYPE</th>
                          <th>Amount</th>
                        </tr>
                        <tr>
                          <td>CASH</td>
                          <td>${analysisData.cash.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>DUES</td>
                          <td>${analysisData.dues.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>ONLINE</td>
                          <td>${analysisData.online.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>CARD</td>
                          <td>${analysisData.card.toFixed(2)}</td>
                        </tr>
                        <tr class="total-row" style="border-top: 2px solid #000;">
                          <td><strong>TOTAL</strong></td>
                          <td><strong>${analysisData.settlementTotal.toFixed(2)}</strong></td>
                        </tr>
                      </table>
                    </div>
                    
                    <p style="text-align: center; margin-top: 30px;">
                      Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
                    </p>
                  </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.focus();
              printWindow.print();
            }}>
              Print Report
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={3}
        p={5}
        mb={10}
      >
        <Input
          width="40%"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Input
          width="40%"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <Button colorScheme="purple" onClick={updateBillDates}>
          Update Bill Dates
        </Button>
        <Button colorScheme="blue" onClick={handleToggle}>
          {toggle ? "Hide List" : "Show List"}
        </Button>

        {toggle && (
          <Box width="20%" position="relative">
            <Input
              placeholder="Search Item"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <List
              position="absolute"
              bg="white"
              zIndex="1"
              width="100%"
              maxHeight="150px"
              overflowY="auto"
            >
              {filteredFoodData.map((item, index) => (
                <ListItem
                  key={index}
                  bg={index === highlightedIndex ? "gray.200" : "white"}
                  padding="5px"
                  cursor="pointer"
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  onClick={() => setSearchItem(item.name)}
                >
                  {item.name}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Button colorScheme="green" onClick={exportToExcel}>
          Export Excel
        </Button>
        <Button colorScheme="red" onClick={exportToPDF}>
          Export Pdf
        </Button>
      </Box>
      <Box mb={4}>
        <HStack spacing={2}>
          <Button
            colorScheme="orange"
            size="sm"
            onClick={() => handlePrint("food")}
          >
            Print Food
          </Button>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => handlePrint("wine")}
          >
            Print Wine
          </Button>
          <Button
            colorScheme="green"
            size="sm"
            onClick={() => handlePrint("total")}
          >
            Print Total
          </Button>
          <Button
            colorScheme="purple"
            size="sm"
            onClick={() => handlePrint("payment")}
          >
            Print Payment
          </Button>
          {/* NEW: Analyze Report Button */}
          <Button
            colorScheme="teal"
            size="sm"
            onClick={onOpen}
            isDisabled={!startDate || !endDate}
          >
            View Analyze Report
          </Button>
        </HStack>
      </Box>
      {/* SALES TABLES */}
      <Box width="full" mt={150} bg="#E3D9FC">
        <TableContainer width="full" p={2}>
          <Table size="sm" variant="striped" colorScheme="green">
            <TableCaption>Daily Sales Summary</TableCaption>
            <Thead>
              <Tr>
                <Th>Food Item</Th>
                <Th>Wine Item</Th>
                <Th>Payment</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Td>
                {/* FOOD SALES */}
                <Table size="xsm" variant="striped" colorScheme="orange">
                  <TableCaption>Food Sales Summary</TableCaption>
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Rate</Th>
                      <Th>Qty</Th>
                      <Th>Amount</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((item, i) => (
                      <Tr key={i}>
                        <Td>{item._id}</Td>
                        <Td>{item.rate}</Td>
                        <Td>{item.totalQuantity}</Td>
                        <Td>{item.totalAmount}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                  <Tfoot>
                    <Tr>
                      <Td
                        colSpan={3}
                        style={{ textAlign: "right", fontWeight: "bold" }}
                      >
                        Total Qty:{" "}
                        {data.reduce((t, i) => t + i.totalQuantity, 0)}
                      </Td>
                      <Td style={{ textAlign: "right", fontWeight: "bold" }}>
                        Total Amt: {data.reduce((t, i) => t + i.totalAmount, 0)}
                      </Td>
                    </Tr>
                  </Tfoot>
                </Table>
              </Td>
              <Td>
                {/* WINE SALES */}
                <Table size="xsm" variant="striped" colorScheme="blue">
                  <TableCaption>Wine Sales Summary</TableCaption>
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Rate</Th>
                      <Th>Qty</Th>
                      <Th>Amount</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {wineData.map((item, i) => (
                      <Tr key={i}>
                        <Td>{item._id}</Td>
                        <Td>{item.rate}</Td>
                        <Td>{item.totalQuantity}</Td>
                        <Td>{item.totalAmount}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                  <Tfoot>
                    <Tr>
                      <Td
                        colSpan={3}
                        style={{ textAlign: "right", fontWeight: "bold" }}
                      >
                        Total Qty:{" "}
                        {wineData.reduce((t, i) => t + i.totalQuantity, 0)}
                      </Td>
                      <Td style={{ textAlign: "right", fontWeight: "bold" }}>
                        Total Amt:{" "}
                        {wineData.reduce((t, i) => t + i.totalAmount, 0)}
                      </Td>
                    </Tr>
                  </Tfoot>
                </Table>
              </Td>
              <Td>
                {/* PAYMENT SUMMARY */}
                <Table size="sm" variant="striped" colorScheme="yellow">
                  <TableCaption>Payment Summary</TableCaption>
                  <Thead>
                    <Tr>
                      <Th>Cash</Th>
                      <Th>Online</Th>
                      <Th>Card</Th>
                      <Th>Discount</Th>
                      <Th>Happy Discount</Th>
                      <Th>Gst</Th>
                      <Th>Due</Th>
                      <Th>NetAmount</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>{payment.cashTotal}</Td>
                      <Td>{payment.onlineTotal}</Td>
                      <Td>{payment.cardTotal}</Td>
                      <Td>{payment?.discountTotal?.toFixed(2)}</Td>
                      <Td>{payment?.happyHourDiscount}</Td>
                      <Td>{payment.gstTotal?.toFixed(2)}</Td>
                      <Td>{payment.dueTotal}</Td>
                      <Td>
                        {payment.cashTotal +
                          payment.onlineTotal +
                          payment.cardTotal}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Td>
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default DailySalesSummary;